/**
 * Session data (docs/04-api.md "Auth & me"). `useMe` is the one query the whole app's routing
 * guards key off of — signed out, no household, or ready to use.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  OnboardingProgress,
  OnboardingStep,
  TourSection,
  UserPreferences,
} from '@finbob/shared';
import { api, isUnauthenticated } from '@/lib/api-client';
import { usePreferencesStore, type PreferenceSave } from '@/stores/preferences.store';

export interface Membership {
  householdId: string;
  role: 'owner' | 'member';
  joinedAt: string;
  onboarding?: OnboardingProgress;
  tourSeen?: TourSection[];
  tourCompleted?: TourSection[];
}

export interface Me {
  id: string;
  email: string;
  name: string;
  avatarUrl?: string;
  memberships: Membership[];
  lastActiveHouseholdId?: string;
  preferences: UserPreferences;
  lastLoginAt: string;
}

export const meQueryKey = ['me'] as const;

export function useMe() {
  return useQuery({
    queryKey: meQueryKey,
    queryFn: () => api.get<Me>('/me'),
    retry: (failures, error) => !isUnauthenticated(error) && failures < 3,
    retryDelay: (attempt) => Math.min(500 * 2 ** attempt, 2000),
    refetchOnReconnect: true,
    refetchInterval: (query) =>
      query.state.status === 'error' && !isUnauthenticated(query.state.error) ? 3000 : false,
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { successMessage: 'Signed out' },
    mutationFn: () => api.post<void>('/auth/logout'),
    onSettled: () => {
      usePreferencesStore.getState().resetSession();
      queryClient.setQueryData(meQueryKey, undefined);
      queryClient.clear();
    },
  });
}

export function useDeleteMyAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { successMessage: 'Account deleted' },
    mutationFn: (email: string) => api.delete<void>('/me', { email }),
    onSuccess: () => {
      usePreferencesStore.getState().resetSession();
      queryClient.setQueryData(meQueryKey, undefined);
      queryClient.clear();
    },
  });
}

export function useUpdatePreferences() {
  const queryClient = useQueryClient();
  const epoch = usePreferencesStore((state) => state.sync.epoch);
  const mutation = useMutation({
    meta: { successMessage: false, errorMessage: false },
    mutationKey: ['appearance-preferences', epoch],
    scope: { id: 'appearance-preferences' },
    mutationFn: async (save: PreferenceSave) => {
      const { sync } = usePreferencesStore.getState();
      // A queued request from a previous login must never run with another user's cookie.
      if (sync.ownerId !== save.ownerId || sync.epoch !== save.epoch) return undefined;
      try {
        return await api.patch<Me>('/me/preferences', save.patch);
      } catch (error) {
        const latest = usePreferencesStore.getState().sync;
        if (latest.ownerId !== save.ownerId || latest.epoch !== save.epoch) return undefined;
        throw error;
      }
    },
    onMutate: () => queryClient.cancelQueries({ queryKey: meQueryKey, exact: true }),
    onSuccess: async (me, save) => {
      if (!me) return;
      const current = usePreferencesStore.getState().sync;
      if (current.ownerId !== save.ownerId || current.epoch !== save.epoch) return;
      await queryClient.cancelQueries({ queryKey: meQueryKey, exact: true });
      const store = usePreferencesStore.getState();
      if (me.id !== save.ownerId) {
        store.failSave(save);
        void queryClient.invalidateQueries({ queryKey: meQueryKey, exact: true });
        return;
      }
      if (!store.confirmSave(save, me.preferences)) return;
      // Preserve session fields from other operations, and merge only confirmed preference
      // fields. Newer per-field previews remain layered above this cache value.
      queryClient.setQueryData<Me>(meQueryKey, (previous) =>
        previous?.id === save.ownerId
          ? { ...previous, preferences: usePreferencesStore.getState().sync.confirmed }
          : previous,
      );
    },
    onError: (_error, save) => usePreferencesStore.getState().failSave(save),
  });
  return {
    ...mutation,
    mutate: (patch: Partial<UserPreferences>) => {
      const save = usePreferencesStore.getState().prepareSave(patch);
      if (save) mutation.mutate(save);
    },
    mutateAsync: async (patch: Partial<UserPreferences>) => {
      const save = usePreferencesStore.getState().prepareSave(patch);
      return save ? mutation.mutateAsync(save) : undefined;
    },
  };
}

export function useSetActiveHousehold() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { successMessage: false },
    mutationFn: (householdId: string) => api.post<Me>('/me/active-household', { householdId }),
    onSuccess: (me) => queryClient.setQueryData(meQueryKey, me),
  });
}

export interface PendingInvite {
  id: string;
  token: string;
  householdId: string;
  householdName: string;
  role: 'owner' | 'member';
  invitedBy: string;
  expiresAt: string;
  createdAt: string;
}

export function useMyInvites(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['me', 'invites'] as const,
    queryFn: () => api.get<PendingInvite[]>('/me/invites'),
    enabled: options.enabled ?? true,
    refetchOnWindowFocus: 'always',
    refetchInterval: 30_000,
  });
}

export function useAcceptInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { successMessage: 'Invitation accepted' },
    mutationFn: (token: string) =>
      api.post<{ householdId: string; role: string }>(`/invites/${token}/accept`),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: meQueryKey }),
        queryClient.invalidateQueries({ queryKey: ['households'] }),
      ]);
    },
  });
}

export function useSaveOnboarding() {
  const client = useQueryClient();
  return useMutation({
    meta: { successMessage: false },
    mutationFn: (input: {
      householdId: string;
      step: OnboardingStep;
      reviewedStep?: OnboardingStep;
      complete?: boolean;
    }) => api.patch<Me>('/me/onboarding', input),
    onSuccess: (me) => client.setQueryData(meQueryKey, me),
  });
}

export function useSaveTour() {
  const client = useQueryClient();
  return useMutation({
    meta: { successMessage: false },
    mutationFn: (input: { householdId: string; section: TourSection; completed?: boolean }) =>
      api.patch<Me>('/me/tour', input),
    // Refetch rather than replacing /me with a possibly older concurrent mutation response.
    onSuccess: () => client.invalidateQueries({ queryKey: meQueryKey }),
  });
}
